import React, { useState } from "react";
import axios from "axios";
import { Card, CardContent } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Plus, Search } from "lucide-react";
import { CouponDiagnostics } from "./CouponDiagnostics";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Admin Promos tab — manage discount coupons and promo banners.
 * Includes inline modal for create/edit coupon.
 */
export const PromosTab = ({
  coupons,
  articles,
  fetchCoupons,
  editingCoupon,
  setEditingCoupon,
  showCouponModal,
  setShowCouponModal,
}) => {
  const [diagnosticSelection, setDiagnosticSelection] = useState(null);
  const isEnabled = (coupon) => (coupon.active === true || coupon.is_active === true) && coupon.active !== false && coupon.is_active !== false;
  return (
    <>
      <div className="mb-8 flex flex-wrap gap-4 justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-white">Promozioni e Codici Sconto</h2>
          <p className="text-gray-400">Gestisci coupon, sconti e banner promozionali</p>
        </div>
        <Button
          onClick={() => {
            const newCoupon = {
              code: "",
              discount_type: "percentage",
              discount_value: "",
              applicable_articles: [],
              expiry_date: "",
              max_uses: 0,
              banner_text: "",
              show_banner: false,
            };
            setEditingCoupon(newCoupon);
            setShowCouponModal(true);
          }}
          data-testid="create-coupon-btn"
        >
          <Plus className="w-4 h-4 mr-2" /> Nuovo Coupon
        </Button>
      </div>

      <CouponDiagnostics selected={diagnosticSelection} />

      {coupons.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-gray-500">Nessun coupon creato. Clicca "Nuovo Coupon" per iniziare!</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {coupons.map((coupon) => (
            <Card key={coupon.id} className={`${!isEnabled(coupon) ? "opacity-60" : ""} min-w-0`}>
              <CardContent className="p-5">
                <div className="flex flex-wrap gap-2 justify-between items-start mb-3">
                  <div className="min-w-0">
                    <h3 className="text-lg font-bold text-gray-900 font-mono break-all" data-testid={`coupon-code-${coupon.id}`}>{coupon.code}</h3>
                    <p className="text-sm text-gray-600">
                      {coupon.discount_type === "percentage" || (!coupon.discount_type && coupon.discount_percent)
                        ? `${coupon.discount_value ?? coupon.discount_percent}% di sconto`
                        : coupon.free_shipping && !coupon.discount_type
                          ? "Spedizione gratuita"
                          : `€${coupon.discount_value ?? coupon.discount_amount ?? 0} di sconto`}
                    </p>
                  </div>
                  <span
                    data-testid={`coupon-enabled-${coupon.id}`}
                    className={`px-2 py-1 rounded-full text-xs font-medium ${isEnabled(coupon) ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}
                  >
                    {isEnabled(coupon) ? "Abilitato" : "Disattivato"}
                  </span>
                </div>
                {coupon.telegram_exclusive && (
                  <div className="bg-sky-50 border border-sky-200 rounded p-2 mb-3 text-sm text-sky-800" data-testid={`coupon-telegram-badge-${coupon.id}`}>
                    📣 Esclusivo iscritti Telegram — un solo sconto a persona
                  </div>
                )}
                {coupon.target_email && (
                  <div className="bg-pink-50 border border-pink-200 rounded p-2 mb-3 text-sm text-pink-800 break-all" data-testid={`coupon-target-badge-${coupon.id}`}>
                    👤 Personale: {coupon.target_email}
                  </div>
                )}
                {coupon.banner_text && coupon.show_banner && (
                  <div className="bg-amber-50 border border-amber-200 rounded p-2 mb-3 text-sm text-amber-800">
                    Banner: {coupon.banner_text}
                  </div>
                )}
                <div className="text-xs text-gray-500 space-y-1 mb-3">
                  <p data-testid={`coupon-uses-${coupon.id}`}>
                    Utilizzi: {coupon.current_uses || 0}
                    {coupon.max_uses > 0 ? ` / ${coupon.max_uses}` : " (illimitati)"}
                  </p>
                  {(coupon.expires_at || coupon.expiry_date) && <p data-testid={`coupon-expiry-${coupon.id}`}>Scadenza: {new Date(coupon.expires_at || coupon.expiry_date).toLocaleDateString("it-IT")}</p>}
                  <p>
                    Articoli:{" "}
                    {coupon.applicable_articles?.length > 0
                      ? `${coupon.applicable_articles.length} selezionati`
                      : "Tutti"}
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button type="button" size="sm" variant="outline" data-testid={`coupon-inspect-${coupon.id}`} onClick={() => setDiagnosticSelection({ code: coupon.code })}><Search className="w-4 h-4 mr-1" />Verifica utilizzi</Button>
                  <Button
                    data-testid={`coupon-toggle-${coupon.id}`}
                    size="sm"
                    variant="outline"
                    onClick={async () => {
                      try {
                        await axios.put(`${API}/coupons/${coupon.id}`, { active: !isEnabled(coupon) });
                        fetchCoupons();
                      } catch (e) {
                        console.error(e);
                      }
                    }}
                  >
                    {isEnabled(coupon) ? "Disattiva" : "Attiva"}
                  </Button>
                  <Button
                    data-testid={`coupon-edit-${coupon.id}`}
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setEditingCoupon({ ...coupon });
                      setShowCouponModal(true);
                    }}
                  >
                    Modifica
                  </Button>
                  <Button
                    data-testid={`coupon-delete-${coupon.id}`}
                    size="sm"
                    variant="outline"
                    className="text-red-600 border-red-300"
                    onClick={async () => {
                      if (window.confirm(`Eliminare il coupon ${coupon.code}?`)) {
                        await axios.delete(`${API}/coupons/${coupon.id}`);
                        fetchCoupons();
                      }
                    }}
                  >
                    Elimina
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Coupon Create/Edit Modal */}
      {showCouponModal && editingCoupon && (
        <div
          data-testid="coupon-editor-modal"
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={() => setShowCouponModal(false)}
        >
          <div
            className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-xl font-bold mb-4">
              {editingCoupon.id ? "Modifica Coupon" : "Nuovo Coupon"}
            </h3>
            <div className="space-y-4">
              <div>
                <Label>Codice Sconto *</Label>
                <Input
                  data-testid="coupon-editor-code"
                  value={editingCoupon.code}
                  onChange={(e) => setEditingCoupon({ ...editingCoupon, code: e.target.value.toUpperCase() })}
                  placeholder="ES: PRIMAVERA15"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Tipo Sconto</Label>
                  <select
                    data-testid="coupon-editor-type"
                    className="w-full px-3 py-2 border rounded-md text-sm"
                    value={editingCoupon.discount_type}
                    onChange={(e) => setEditingCoupon({ ...editingCoupon, discount_type: e.target.value })}
                  >
                    <option value="percentage">Percentuale (%)</option>
                    <option value="fixed">Fisso (€)</option>
                  </select>
                </div>
                <div>
                  <Label>Valore Sconto *</Label>
                  <Input
                    data-testid="coupon-editor-value"
                    type="number"
                    min="0"
                    value={editingCoupon.discount_value}
                    onChange={(e) => setEditingCoupon({ ...editingCoupon, discount_value: e.target.value })}
                    placeholder={editingCoupon.discount_type === "percentage" ? "15" : "10.00"}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Data Scadenza</Label>
                  <Input
                    data-testid="coupon-editor-expiry"
                    type="date"
                    value={editingCoupon.expiry_date || ""}
                    onChange={(e) => setEditingCoupon({ ...editingCoupon, expiry_date: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Utilizzi Max (0=illimitati)</Label>
                  <Input
                    data-testid="coupon-editor-max-uses"
                    type="number"
                    min="0"
                    value={editingCoupon.max_uses}
                    onChange={(e) =>
                      setEditingCoupon({ ...editingCoupon, max_uses: parseInt(e.target.value) || 0 })
                    }
                  />
                </div>
              </div>
              <div>
                <Label>Riservato all'email cliente (opzionale)</Label>
                <Input
                  type="email"
                  placeholder="cliente@esempio.it — solo questo cliente potrà usarlo, nessun badge pubblico"
                  value={editingCoupon.target_email || ""}
                  onChange={(e) => setEditingCoupon({ ...editingCoupon, target_email: e.target.value })}
                  data-testid="coupon-target-email-input"
                />
              </div>
              <div>
                <Label>Articoli applicabili</Label>
                <p className="text-xs text-gray-500 mb-2">
                  Seleziona gli articoli su cui si applica lo sconto. Nessuna selezione = tutti gli articoli.
                </p>
                <div className="max-h-40 overflow-y-auto border rounded-md p-2 space-y-1">
                  {articles.map((art) => (
                    <label
                      key={art.id}
                      className="flex items-center gap-2 text-sm cursor-pointer hover:bg-gray-50 p-1 rounded"
                    >
                      <input
                        data-testid={`coupon-editor-article-${art.id}`}
                        type="checkbox"
                        checked={(editingCoupon.applicable_articles || []).includes(art.id)}
                        onChange={(e) => {
                          const ids = editingCoupon.applicable_articles || [];
                          setEditingCoupon({
                            ...editingCoupon,
                            applicable_articles: e.target.checked
                              ? [...ids, art.id]
                              : ids.filter((i) => i !== art.id),
                          });
                        }}
                      />
                      {art.title}
                    </label>
                  ))}
                </div>
              </div>
              <div className="border-t pt-4">
                <h4 className="font-semibold mb-2">Banner Promozionale</h4>
                <div>
                  <Label>Testo Banner</Label>
                  <Input
                    data-testid="coupon-editor-banner-text"
                    value={editingCoupon.banner_text || ""}
                    onChange={(e) => setEditingCoupon({ ...editingCoupon, banner_text: e.target.value })}
                    placeholder="Es: SALDI PRIMAVERA! Usa il codice PRIMAVERA15 per il 15% di sconto!"
                  />
                </div>
                <label className="flex items-center gap-2 mt-2 text-sm cursor-pointer">
                  <input
                    data-testid="coupon-editor-show-banner"
                    type="checkbox"
                    checked={editingCoupon.show_banner || false}
                    onChange={(e) => setEditingCoupon({ ...editingCoupon, show_banner: e.target.checked })}
                  />
                  Mostra banner su homepage e sezione prodotti
                </label>
                <label className="flex items-center gap-2 mt-2 text-sm cursor-pointer" data-testid="coupon-telegram-exclusive-label">
                  <input
                    type="checkbox"
                    data-testid="coupon-telegram-exclusive-checkbox"
                    checked={editingCoupon.telegram_exclusive || false}
                    onChange={(e) => setEditingCoupon({ ...editingCoupon, telegram_exclusive: e.target.checked })}
                  />
                  📣 Solo Telegram (esclusivo iscritti al canale: monouso per email, non cumulabile col benvenuto, nascosto dal sito e pubblicato sul canale)
                </label>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <Button data-testid="coupon-editor-cancel" variant="outline" onClick={() => setShowCouponModal(false)}>
                Annulla
              </Button>
              <Button
                data-testid="coupon-editor-save"
                onClick={async () => {
                  try {
                    if (editingCoupon.id) {
                      await axios.put(`${API}/coupons/${editingCoupon.id}`, editingCoupon);
                    } else {
                      await axios.post(`${API}/coupons`, editingCoupon);
                    }
                    setShowCouponModal(false);
                    fetchCoupons();
                  } catch (err) {
                    alert(err.response?.data?.detail || "Errore nel salvataggio");
                  }
                }}
                disabled={!editingCoupon.code || !editingCoupon.discount_value}
              >
                {editingCoupon.id ? "Salva Modifiche" : "Crea Coupon"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
